import {
  Alert,
  Box,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StorageIcon from '@mui/icons-material/Storage';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import JiraStorySelector from './JiraStorySelector';
import TestSuiteSelector from './TestSuiteSelector';

const ACCEPTED_EXT = '.pdf,.docx,.txt,.md, .doc';
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_TEXT_CHARS = 10000;
const MIN_TEXT_CHARS = 20;

const formatBytes = (b) =>
  b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;

// ── Source modes ──────────────────────────────────────────────────────────────

const SOURCE_MODES = [
  { value: 'text', icon: <AutoAwesomeIcon sx={{ fontSize: 14 }} />, label: 'Describe' },
  { value: 'jira', icon: <StorageIcon      sx={{ fontSize: 14 }} />, label: 'Jira Story' },
  // { value: 'url',  icon: <LinkIcon         sx={{ fontSize: 14 }} />, label: 'URL' },
  { value: 'file', icon: <UploadFileIcon   sx={{ fontSize: 14 }} />, label: 'Upload File' },
];

// const QUANTITY_OPTIONS = [
//   { value: 'multiple', icon: <FormatListNumberedIcon sx={{ fontSize: 16 }} />, label: 'Multiple' },
//   { value: 'single',   icon: <LooksOneIcon           sx={{ fontSize: 16 }} />, label: 'Single' },
// ];

const TOGGLE_BTN_SX = {
  px: 1.5,
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  borderColor: 'divider',
  '&.Mui-selected': { bgcolor: '#EFF6FF', color: 'primary.main', borderColor: 'primary.main' },
};

const FieldLabel = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={600}
    color="text.secondary"
    display="block"
    sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '11px' }}
  >
    {children}
  </Typography>
);

const DropZone = ({ file, dragOver, fileInputRef, onDragOver, onDragLeave, onDrop, onClick, onFileChange, onClear }) => (
  <Box
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    onClick={onClick}
    sx={{
      border: '2px dashed',
      borderRadius: 1.5,
      px: 3, py: 3,
      textAlign: 'center',
      cursor: 'pointer',
      borderColor: dragOver ? 'primary.main' : file ? 'success.light' : 'divider',
      bgcolor: dragOver ? 'primary.50' : file ? '#f0fdf4' : 'grey.50',
      transition: 'border-color 0.15s, background 0.15s',
      '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
    }}
  >
    <input ref={fileInputRef} type="file" accept={ACCEPTED_EXT} style={{ display: 'none' }} onChange={onFileChange} />
    {file ? (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <InsertDriveFileIcon sx={{ fontSize: 20, color: 'success.main' }} />
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body2" fontWeight={600} color="success.dark">{file.name}</Typography>
          <Typography variant="caption" color="text.secondary">{formatBytes(file.size)}</Typography>
        </Box>
        <Box
          component="span"
          onClick={(e) => { e.stopPropagation(); onClear(); }}
          sx={{ ml: 1, fontSize: '12px', color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}
        >
          Change
        </Box>
      </Box>
    ) : (
      <>
        <UploadFileIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          Drag & drop or{' '}
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>browse</Box>
        </Typography>
        <Typography variant="caption" color="text.disabled">PDF · DOCX · TXT · MD — up to 5 MB</Typography>
      </>
    )}
  </Box>
);

// ── AiInputScreen ─────────────────────────────────────────────────────────────

const AiInputScreen = ({
  textInput,
  setTextInput,
  aiError,
  quantity,
  onQuantityChange,
  testSuites = [],
  selectedTestSuites = [],
  onTestSuitesChange,
  sourceMode = 'text',
  onSourceModeChange,
  currentProject,
  linkedJiraProjects,
  onPayloadChange,
}) => {
  const { projectId } = useParams();

  const [url,       setUrl]       = useState('');
  const [file,      setFile]      = useState(null);
  const [fileError, setFileError] = useState('');
  const [dragOver,  setDragOver]  = useState(false);
  const [jiraStory, setJiraStory] = useState(null);
  const [jiraContext, setJiraContext] = useState({ epicContext: null, confluencePages: [] });
  const fileInputRef = useRef(null);

  // ── Payload computation ───────────────────────────────────────────────────
  // Centralised — called with the final resolved values so there are no stale-closure issues.
  const computeAndReport = ({
    sm = sourceMode, ti = textInput, q = quantity,
    u = url, f = file, fe = fileError,
    js = jiraStory, jc = jiraContext,
    sns = selectedTestSuites,
  } = {}) => {
    const suitesSelected = sns.length > 0;
    let canGenerate = false;
    let payload = null;

    switch (sm) {
      case 'text':
        canGenerate = ti.trim().length >= MIN_TEXT_CHARS && (q === 'single' || suitesSelected);
        payload = { description: ti.trim(), mode: q };
        break;
      case 'url':
        canGenerate = u.trim().startsWith('https://') && suitesSelected;
        payload = { mode: q, content: u.trim(), isUrl: true };
        break;
      case 'file':
        canGenerate = !!f && !fe && suitesSelected;
        payload = { mode: q, file: f };
        break;
      case 'jira':
        canGenerate = !!js && !!js.description && suitesSelected;
        payload = { mode: q, story: js, epicContext: jc.epicContext, confluencePages: jc.confluencePages };
        break;
      default:
        break;
    }

    onPayloadChange?.({ payload, canGenerate });
  };

  const handleSourceChange = (_, val) => {
    if (!val) return;
    onSourceModeChange(val);
    setFileError('');
    computeAndReport({ sm: val });
  };

  const handleTextChange = (val) => {
    if (val.length > MAX_TEXT_CHARS) return;
    setTextInput(val);
    computeAndReport({ ti: val });
  };

  // const handleQuantityChange = (val) => {
  //   onQuantityChange(val);
  //   computeAndReport({ q: val });
  // };

  const handleUrlChange = (val) => {
    setUrl(val);
    computeAndReport({ u: val });
  };

  const handleFileValidate = (f) => {
    setFileError('');
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) {
      const err = `File too large (${formatBytes(f.size)}). Maximum is 5 MB.`;
      setFileError(err);
      computeAndReport({ f: null, fe: err });
      return;
    }
    setFile(f);
    computeAndReport({ f, fe: '' });
  };

  const handleStorySelect = (story) => {
    setJiraStory(story);
    computeAndReport({ js: story });
  };

  const handleContextChange = (ctx) => {
    setJiraContext(ctx);
    computeAndReport({ jc: ctx });
  };

  const handleSuitesChange = (ids) => {
    onTestSuitesChange(ids);
    computeAndReport({ sns: ids });
  };

  const suiteSelector = (
    <TestSuiteSelector
      testSuites={testSuites}
      selectedTestSuites={selectedTestSuites}
      onTestSuitesChange={handleSuitesChange}
      projectId={projectId}
    />
  );

  // const quantityToggle = (
  //   <Box>
  //     <FieldLabel>How many test cases?</FieldLabel>
  //     <ToggleButtonGroup
  //       value={quantity}
  //       exclusive
  //       onChange={(_, val) => { if (val) handleQuantityChange(val); }}
  //       size="small"
  //       sx={{ '& .MuiToggleButton-root': TOGGLE_BTN_SX }}
  //     >
  //       {QUANTITY_OPTIONS.map(({ value, icon, label }) => (
  //         <ToggleButton key={value} value={value} sx={{ gap: 0.75 }}>
  //           {icon}{label}
  //         </ToggleButton>
  //       ))}
  //     </ToggleButtonGroup>
  //     <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
  //       {quantity === 'multiple'
  //         ? 'Generates a full suite — happy path, edge cases, and negative scenarios.'
  //         : 'Generates one focused, detailed test case.'}
  //     </Typography>
  //   </Box>
  // );

  const tooShort = textInput.length > 0 && textInput.length < MIN_TEXT_CHARS;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1.5 }}>

      {/* Source tabs */}
      <Box>
        <FieldLabel>Input source</FieldLabel>
        <ToggleButtonGroup
          value={sourceMode}
          exclusive
          onChange={handleSourceChange}
          size="small"
          sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': TOGGLE_BTN_SX }}
        >
          {SOURCE_MODES.map(({ value, icon, label }) => (
            <ToggleButton key={value} value={value} sx={{ gap: 0.5 }}>
              {icon}{label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* ── Text / Describe ──────────────────────────────────────────────── */}
      {sourceMode === 'text' && (
        <>
          {/* {quantityToggle} */}
          <TextField
            multiline
            rows={6}
            fullWidth
            label="Requirements or scenario"
            placeholder="Describe what to test, or paste your requirements, acceptance criteria, or user story here…"
            value={textInput}
            onChange={(e) => handleTextChange(e.target.value)}
            helperText={
              tooShort
                ? `Too short — enter at least ${MIN_TEXT_CHARS} characters (${textInput.length}/${MIN_TEXT_CHARS})`
                : `${textInput.length.toLocaleString()} / ${MAX_TEXT_CHARS.toLocaleString()} characters`
            }
            autoFocus
            inputProps={{ 'aria-label': 'Test scenario or requirements' }}
          />
          {aiError &&
            sourceMode === 'text' &&
            textInput?.trim().length < 80 && (
              <Alert severity="info">
                <strong>Tip:</strong> Adding requirements, acceptance criteria, business rules, or workflow details usually helps generate more comprehensive test cases.
              </Alert>
          )}
          {suiteSelector}
          {aiError && <Alert severity="error" sx={{ py: 0 }}>{aiError}</Alert>}
        </>
      )}

      {/* ── Jira Story ───────────────────────────────────────────────────── */}
      {sourceMode === 'jira' && (
        <>
          {/* {quantityToggle} */}
          <JiraStorySelector
            currentProject={currentProject}
            linkedJiraProjects={linkedJiraProjects}
            testSuites={testSuites}
            selectedTestSuites={selectedTestSuites}
            onTestSuitesChange={handleSuitesChange}
            selectedStory={jiraStory}
            onStorySelect={handleStorySelect}
            generateError={aiError}
            onContextChange={handleContextChange}
          />
          {aiError && <Alert severity="error" sx={{ py: 0, mt: 1 }}>{aiError}</Alert>}
        </>
      )}

      {/* ── URL ──────────────────────────────────────────────────────────── */}
      {sourceMode === 'url' && (
        <>
          {/* {quantityToggle} */}
          <Typography variant="caption" color="text.secondary">
            Paste a link to a spec, Confluence page, or any publicly accessible document. The page content will be fetched automatically — private or login-protected pages are not supported.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Page URL"
            placeholder="https://…"
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            error={!!url && !url.trim().startsWith('https://')}
            helperText={!!url && !url.trim().startsWith('https://') ? 'URL must start with https://' : ''}
            inputProps={{ 'aria-label': 'Content URL', type: 'url' }}
          />
          {aiError && <Alert severity="error" sx={{ py: 0 }}>{aiError}</Alert>}
          {suiteSelector}
        </>
      )}

      {/* ── File Upload ───────────────────────────────────────────────────── */}
      {sourceMode === 'file' && (
        <>
          {/* {quantityToggle} */}
          <Typography variant="caption" color="text.secondary">
            Upload a PDF, DOCX, TXT, or Markdown file (max 5 MB).
          </Typography>
          <DropZone
            file={file}
            dragOver={dragOver}
            fileInputRef={fileInputRef}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileValidate(e.dataTransfer.files?.[0]); }}
            onClick={() => fileInputRef.current?.click()}
            onFileChange={(e) => handleFileValidate(e.target.files?.[0])}
            onClear={() => { setFile(null); setFileError(''); computeAndReport({ f: null, fe: '' }); }}
          />
          {fileError && <Alert severity="error" sx={{ py: 0.5 }}>{fileError}</Alert>}
          {aiError   && <Alert severity="error" sx={{ py: 0 }}>{aiError}</Alert>}
          {file && !fileError && suiteSelector}
        </>
      )}
    </Box>
  );
};

export default AiInputScreen;